
import {useCallback, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useModalStore } from "@/services/modalStore";
import { CheckCircle, Star } from "lucide-react";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { trackPremiumPaywallView } from "@/lib/analyticsEvents";

interface PremiumFeatureInfo {
  key: string;
  title: string;
  description: string;
}

const PREMIUM_FEATURES: Record<string, PremiumFeatureInfo> = {
  'unlimited_lists': {
    key: 'unlimited_lists',
    title: 'Liste Illimitate',
    description: 'Crea un numero illimitato di liste della spesa per diversi negozi o scopi'
  },
  'list_backgrounds': {
    key: 'list_backgrounds',
    title: 'Sfondi Personalizzati',
    description: 'Personalizza lo sfondo delle tue liste della spesa con colori o gradienti'
  },
  'product_photos': {
    key: 'product_photos',
    title: 'Foto dei Prodotti',
    description: 'Aggiungi foto ai tuoi prodotti per riconoscerli più facilmente'
  },
  'custom_themes': {
    key: 'custom_themes',
    title: 'Temi Personalizzati',
    description: 'Cambia il tema dell\'app secondo i tuoi gusti'
  },
  'list_store_binding': {
    key: 'list_store_binding',
    title: 'Associazione Negozi',
    description: 'Collega un negozio specifico a ciascuna lista'
  },
  'list_sharing': {
    key: 'list_sharing',
    title: 'Condivisione Liste',
    description: 'Condividi le tue liste della spesa con amici e familiari'
  }
};

const PremiumUpgradeModal: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useThemeConfig();
  const {
    showPremiumModal,
    setShowPremiumModal,
    lastPremiumFeatureAttempt
  } = useModalStore();


  useEffect(() => {
    if (showPremiumModal) {
      trackPremiumPaywallView(lastPremiumFeatureAttempt ?? undefined);
    }
  }, [showPremiumModal, lastPremiumFeatureAttempt]);

  const featureInfo = lastPremiumFeatureAttempt
    ? PREMIUM_FEATURES[lastPremiumFeatureAttempt] || {
        key: 'premium',
        title: 'Funzionalità Premium',
        description: 'Accedi a tutte le funzionalità avanzate dell\'app'
      }
    : {
        key: 'premium',
        title: 'Funzionalità Premium',
        description: 'Accedi a tutte le funzionalità avanzate dell\'app'
      };

  const handleClose = () => setShowPremiumModal(false);
  const handleUpgrade = () => {
    setShowPremiumModal(false);
    navigate('/premium');
  };
  return (
    <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
            <DialogContent
        id="premium-upgrade-modal"
        aria-describedby="premium-dialog-description"
        className="sm:max-w-md"
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.backgroundColor || '#ffffff',
          color: theme.textColor || '#000000',
        }}
      >
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Star className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <DialogTitle className="text-center">{featureInfo.title}</DialogTitle>
          {featureInfo.description && (
            <DialogDescription id="premium-dialog-description" className="text-center" style={{ color: theme.sectionTitleColor || "#000000" }}>
              {featureInfo.description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-3 my-2">
          <p className="text-center font-medium">Con Premium ottieni:</p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>Liste della spesa illimitate</span>
            </li>
            {/* <li className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>Condivisione delle liste con altri utenti</span>
            </li> */}
            <li className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>Foto per i prodotti</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>Analisi della spesa e statistiche</span>
            </li>
          </ul>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={handleClose}>
            Non ora
          </Button>
        <Button
          onClick={handleUpgrade}
          style={{
            backgroundColor: theme.primaryColor,
            borderRadius: theme.buttonBorderRadius || '0.375rem',
            color: theme.textPrimaryColor,
            fontWeight: 'bold',
          }}
        >
          Passa a Premium
        </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default PremiumUpgradeModal;
